<template>
  <div :player-state="playerState">
    <VideoPlayer
      ref="videoPlayerRef"
      class="video-player"
      v-if="videoSrc !== ''"
      :streamingType="props.streamingInfo.type"
      :src="videoSrc"
      @video-seeked="onVideoSeeked"
      @video-seeking="onVideoSeeking"
      @video-paused="onVideoPaused"
      @video-played="onVideoPlayed"
      @player-error="handlePlayerError"
      @player-start="handlePlayerStart"
      @player-ended="handlePlayerEnded"
      @player-unexpectedly-ended="handlePlayerUnexpectedlyEnded"
      @token-refresh="refreshToken"
      :force-reload-when-src-change="forceReloadWhenSrcChange"
    />
    <div class="overlay" v-if="showOverLay">
      <div class="content-box" v-if="!isStreamStarted">
        <img :src="startingSoonImagePath" alt="starting soon" @error="handleImageMissing" />
      </div>
      <div class="content-box" v-else-if="isStreamEnded">
        <img :src="endedImagePath" alt="ended" />
      </div>
      <div class="content-box" v-else>
        <i class="bi bi-exclamation-circle"></i>
        <div class="content-text">
          <p v-if="!isInternetConnected">{{ $t('streamingVideo.key018') }}</p>
          <p v-else-if="retryFetchVideoInterval !== null">{{ $t('streamingVideo.key019') }}</p>
          <p v-else>{{ $t('streamingVideo.key020') }}</p>
          <p v-if="retryFetchVideoInterval !== null" class="connection-loading">{{ $t('streamingVideo.key021') }}</p>
          <button v-else class="btn-reconnect" @click="handleReconnect">
            <i class="bi bi-arrow-counterclockwise"></i>
            {{ $t('streamingVideo.key022') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onBeforeUnmount, onMounted, computed, inject, defineEmits, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { CUSTOM_ERROR_CODE } from '@/utils/validation.js';
import VideoPlayer from '@/components/VideoPlayer.vue';
import { getVideoPlayUrl, refreshVodToken } from '@/api/streamingService.js';
import { HttpStatusCode } from 'axios';
import { PlayerState } from '@/components/constants/videoPlayer.js';
import { showCustomAlert } from '@/utils/alertController';
import startingSoonImage from '@/assets/img/streaming/streaming_default_starting_soon.jpg';
import endedImage from '@/assets/img/streaming/streaming_default_ended.jpg';

// 12 * 5 = 1 minutes
const MAX_RETRY_TIMES = 12;

const RETRY_FETCH_VIDEO_INTERVAL = 5000;

const store = useStore();
const router = useRouter();

const handleCompetedLogin = inject('handleCompetedLogin');

const videoSrc = ref('');
const videoPlayerRef = ref(null);
// first time need change src
const forceReloadWhenSrcChange = ref(true);
const isFetchingVideoSrc = ref(false);
const retryFetchVideoInterval = ref(null);
const retryTimes = ref(0);
const isFetchingVodToken = ref(false);

const isUnmounted = ref(false);
const isInternetConnected = ref(true);

const emit = defineEmits(['refresh-streaming-info', 'ticket-check-failed-fallback', 'video-seeked', 'video-seeking', 'video-paused', 'video-played']);

const props = defineProps({
  /**
   * @description 節目資訊
   * @property {string} cid - 節目ID
   * @property {boolean} needLogin - 是否需要登入
   * @property {string} startTime - 節目開始時間
   * @property {string} endTime - 節目結束時間
   */
  streamingInfo: {
    type: Object,
    required: true,
  },
});

const playerState = computed(() => videoPlayerRef.value?.playerState);

const cloudFront = process.env.VUE_APP_CLOUD_FRONT;
const startingSoonImagePath = ref(`${cloudFront}/Image/Public/streaming/${props.streamingInfo.roomId}/StreamWebGL_DisplayTime.jpg`);
const endedImagePath = computed(() => endedImage);
const handleImageMissing = (e) => {
  e.target.src = startingSoonImage;
};

const isStreamStarted = computed(() => new Date(props.streamingInfo.startTime) <= new Date());
const isStreamEnded = computed(() => new Date(props.streamingInfo.endTime) <= new Date());

const showOverLay = computed(() => (playerState.value === PlayerState.Error && isStreamStarted.value) || !isStreamStarted.value || isStreamEnded.value);

let tokenRefreshInterval = null;

onMounted(async () => {
  try {
    await fetchVideoSrc();
  } catch (error) {
    console.error(error);
  }
});

onBeforeUnmount(() => {
  isUnmounted.value = true;

  removeRetryFetchVideoInterval();
  stopTokenRefreshTimer();
});

const logOut = () => {
  store.dispatch('logout');

  router.push({
    path: `/${store.state.currentLanguage}/member-login/login`,
  });
};

const handlePlayerError = async (error) => {
  console.error(error);

  if (error?.type === 'ErrorAuthorization') {
    logOut();
  } else if (error?.type !== 'error') {
    if (isStreamStarted.value) {
      setRetryFetchVideoInterval();
    }
  } else {
    console.error('error');
  }
};

const onVideoSeeked = (newTime) => {
  emit('video-seeked', newTime);
};
const onVideoSeeking = () => {
  emit('video-seeking');
};
const onVideoPaused = () => {
  emit('video-paused');
};
const onVideoPlayed = (newTime) => {
  emit('video-played', newTime);
};

// 觀測直播是否開始 開始時取得直播串流Url
watch(
  () => isStreamStarted.value,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      fetchVideoSrc();
    }
  },
);

const fetchVideoSrc = async () => {
  try {
    if (isFetchingVideoSrc.value || isUnmounted.value) {
      return;
    }

    isFetchingVideoSrc.value = true;

    if (props.streamingInfo.needLogin && !store.state.jwt) {
      return;
    }

    if (!isStreamStarted.value) {
      return;
    }

    const result = await getVideoPlayUrl(props.streamingInfo.roomId, props.streamingInfo.needLogin);
    let playUrl = result.data.data;

    // 回放才開啟刷新 Cookie
    if (props.streamingInfo.type == 'replay') {
      playUrl = result.data.data.vodUrl;
      const expireTime = result.data.data.expiresAt;
      startTokenRefreshTimer(expireTime);
    }

    // get playUrl before m3u8
    const newPlayUrlBeforeM3u8 = playUrl.split('.m3u8')[0];
    const oldPlayUrlBeforeM3u8 = videoSrc.value.split('.m3u8')[0];

    // video src origin changed
    if (newPlayUrlBeforeM3u8 !== oldPlayUrlBeforeM3u8) {
      forceReloadWhenSrcChange.value = true;
    }

    if (forceReloadWhenSrcChange.value) {
      // if url is the same, play video directly
      if (videoSrc.value === playUrl) {
        videoPlayerRef.value?.playVideo();
      } else {
        videoSrc.value = playUrl;
      }
    }
  } catch (error) {
    console.error(error);

    if (error?.response?.status === HttpStatusCode.Unauthorized && typeof handleCompetedLogin === 'function') {
      handleCompetedLogin();
    } else if (error?.response?.status === HttpStatusCode.Forbidden && error?.response.data.data.customCode === CUSTOM_ERROR_CODE.STREAMING_NOT_AVAILABLE_IN_YOUR_COUNTRY) {
      await showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key034' });

      router.push({
        path: `/${store.state.currentLanguage}`,
      });

      return;
    } else if (error?.response?.status === HttpStatusCode.UnprocessableEntity && error?.response.data.data.customCode === CUSTOM_ERROR_CODE.INVALID_USER_TICKETS) {
      emit('ticket-check-failed-fallback');
      return;
    }
    // first time fetch error, retry fetch video src
    setRetryFetchVideoInterval();
  } finally {
    isFetchingVideoSrc.value = false;
  }
};

const startTokenRefreshTimer = (expireTimestamp) => {
  stopTokenRefreshTimer();

  const expireDateMs = expireTimestamp * 1000;
  const now = Date.now();
  const timeUntilExpire = expireDateMs - now;

  // 緩衝時間：過期前 2 分鐘刷新
  const BUFFER_MS = 2 * 60 * 1000;
  let refreshDelay = timeUntilExpire - BUFFER_MS;

  tokenRefreshInterval = setTimeout(async () => {
    await refreshToken();
  }, refreshDelay);
};

const refreshToken = async () => {
  if (isFetchingVodToken.value) return;

  try {
    isFetchingVodToken.value = true;
    const res = await refreshVodToken(props.streamingInfo.roomId);
    const newExp = res.data?.data?.expiresAt;

    if (newExp) {
      startTokenRefreshTimer(newExp);
      videoPlayerRef.value?.wakeUpPlayer();
    } else {
      console.warn('刷新 API 未回傳新的過期時間');
    }
  } catch (err) {
    console.error(err);
  } finally {
    isFetchingVodToken.value = false;
  }
};

const stopTokenRefreshTimer = () => {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

const setRetryFetchVideoInterval = () => {
  if (!retryFetchVideoInterval.value) {
    retryFetchVideoInterval.value = setInterval(retryFetchVideoSrc, RETRY_FETCH_VIDEO_INTERVAL);
  }

  forceReloadWhenSrcChange.value = true;
};

const removeRetryFetchVideoInterval = () => {
  if (retryFetchVideoInterval.value) {
    clearInterval(retryFetchVideoInterval.value);
    retryFetchVideoInterval.value = null;
  }

  retryTimes.value = 0;
};

const retryFetchVideoSrc = async (limit = true) => {
  if (retryTimes.value >= MAX_RETRY_TIMES) {
    removeRetryFetchVideoInterval();
    return;
  }

  await checkInternet();

  if (limit) {
    retryTimes.value++;
  }

  fetchVideoSrc();
};

const checkInternet = async () => {
  try {
    await fetch(`https://www.google.com/favicon.ico?_=${Date.now()}`, { mode: 'no-cors', cache: 'no-store' });
    isInternetConnected.value = true;
    return true;
  } catch (error) {
    isInternetConnected.value = false;
    return false;
  }
};

const handlePlayerStart = () => {
  removeRetryFetchVideoInterval();
  isInternetConnected.value = true;
  // if video is playing normally, don't need to change src
  forceReloadWhenSrcChange.value = false;
};

// when video ended, try to reload video src because the video might be unexpectedly interrupted
const handlePlayerEnded = () => {
  setRetryFetchVideoInterval();
  emit('refresh-streaming-info');
};

const handlePlayerUnexpectedlyEnded = () => {
  emit('refresh-streaming-info');
  if (isStreamEnded.value) {
    setRetryFetchVideoInterval();
  } else {
    if (!retryFetchVideoInterval.value) {
      retryFetchVideoInterval.value = setInterval(() => {
        retryFetchVideoSrc(false);
      }, RETRY_FETCH_VIDEO_INTERVAL);
    }

    forceReloadWhenSrcChange.value = true;
  }
};

const handleReconnect = () => {
  videoSrc.value = ''; // 清空資源重置播放器
  emit('video-seeked', 0);
  setRetryFetchVideoInterval();
};
</script>

<style lang="scss"></style>
